import type { AiAvatarMode } from './AiInterviewAvatar';
import { Brain, Loader2, Mic, Sparkles, User } from 'lucide-react';

type PageState = 'loading' | 'ready' | 'error' | 'chat' | 'done';

type AgentPhase = 'ready' | 'thinking' | 'speaking' | 'your_turn' | 'complete';

export function deriveAgentPhase(
  pageState: PageState,
  mode: AiAvatarMode,
  sending: boolean,
  userMessageCount: number,
  assistantMessageCount: number,
): AgentPhase {
  if (pageState === 'done') return 'complete';
  if (sending || mode === 'thinking') return 'thinking';
  if (mode === 'speaking') return 'speaking';
  if (assistantMessageCount === 0 && userMessageCount === 0) return 'ready';
  return 'your_turn';
}

const PHASE_COPY: Record<
  AgentPhase,
  { label: string; sub: string; tone: 'cyan' | 'amber' | 'emerald' | 'sky' }
> = {
  ready: {
    label: "You're connected",
    sub: 'Begin when you feel ready — the conversation adapts to what you share.',
    tone: 'emerald',
  },
  thinking: {
    label: 'One moment',
    sub: 'We are shaping the next question for you.',
    tone: 'amber',
  },
  speaking: {
    label: 'Interviewer',
    sub: 'Listen in comfort — you can reply when the audio finishes.',
    tone: 'cyan',
  },
  your_turn: {
    label: 'Your turn',
    sub: 'Share your thoughts in your own words. Pause or ask for clarity anytime.',
    tone: 'sky',
  },
  complete: {
    label: 'Thank you',
    sub: 'This session is complete. We appreciate your time.',
    tone: 'emerald',
  },
};

/** Light: dark text on tinted surface. Dark: light text on deeper tint (avoid pale-on-pale). */
const toneRing: Record<typeof PHASE_COPY.ready.tone, string> = {
  cyan:
    'border-cyan-600/25 bg-cyan-50 text-cyan-950 dark:border-cyan-500/45 dark:bg-cyan-950/45 dark:text-cyan-50',
  amber:
    'border-amber-600/30 bg-amber-50 text-amber-950 dark:border-amber-500/45 dark:bg-amber-950/40 dark:text-amber-50',
  emerald:
    'border-emerald-600/25 bg-emerald-50 text-emerald-950 dark:border-emerald-500/45 dark:bg-emerald-950/45 dark:text-emerald-50',
  sky: 'border-sky-600/25 bg-sky-50 text-sky-950 dark:border-sky-500/45 dark:bg-sky-950/45 dark:text-sky-50',
};

type Props = {
  pageState: PageState;
  mode: AiAvatarMode;
  sending: boolean;
  userMessageCount: number;
  assistantMessageCount: number;
  assistantTurnIndex: number;
  className?: string;
};

export function AiInterviewAgentBar({
  pageState,
  mode,
  sending,
  userMessageCount,
  assistantMessageCount,
  assistantTurnIndex,
  className = '',
}: Props) {
  const phase = deriveAgentPhase(pageState, mode, sending, userMessageCount, assistantMessageCount);
  const copy = PHASE_COPY[phase];
  const ring = toneRing[copy.tone];

  return (
    <div
      className={`flex flex-wrap items-center gap-2 overflow-hidden rounded-lg border border-violet-200/90 bg-gradient-to-r from-violet-50/90 to-white/95 px-2.5 py-1.5 backdrop-blur-md dark:border-violet-900/45 dark:from-violet-950/40 dark:to-slate-900/75 ${className}`.trim()}
      role="status"
      aria-live="polite"
    >
      <span
        className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400/95"
        title="Connected"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-45 dark:bg-emerald-400" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-600 dark:bg-emerald-400" />
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-400">
          Live
        </span>
      </span>

      <div className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${ring}`}>
        {phase === 'thinking' && (sending ? <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" /> : <Brain className="h-3.5 w-3.5 shrink-0 opacity-90" />)}
        {phase === 'speaking' && <Mic className="h-3.5 w-3.5 shrink-0" />}
        {phase === 'your_turn' && <User className="h-3.5 w-3.5 shrink-0" />}
        {(phase === 'ready' || phase === 'complete') && <Sparkles className="h-3.5 w-3.5 shrink-0" />}
        <span>{copy.label}</span>
      </div>

      {assistantTurnIndex > 0 && phase !== 'complete' && (
        <span className="text-[11px] tabular-nums text-slate-500 dark:text-slate-500">
          Turn <span className="text-slate-800 dark:text-slate-300">{assistantTurnIndex}</span>
        </span>
      )}

      <p className="w-full text-[11px] leading-snug text-slate-700 dark:text-slate-400 sm:w-auto sm:flex-1 sm:min-w-[200px]">
        {copy.sub}
      </p>
    </div>
  );
}
